import React, { useEffect, useRef, useState } from 'react';
import { Check, Download, Loader2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { resolveSeasonCommodity } from '../../../constants/importerSeasonPdf';
import { mintImporterReportDownload } from '../../../utils/api';
import { pdfLangFromI18n } from '../../../constants/importerSeasonPdf';
import {
  hasSeasonExportersSection,
  hasSeasonImportersSection,
} from '../../../utils/seasonAccessGate';
import { resolveSeasonDestKey } from '../../../utils/seasonDestination';
import {
  resolveSeasonChromeState,
  seasonPdfDisabledReason,
  selectSeasonFromActive,
} from '../../../utils/seasonFromActive';
import { formatSeasonSubtitle } from '../../../utils/seasonSubtitle';
import { formatSeasonYoy } from '../../../utils/seasonYoy';
import { pickRegionTables } from '../../../utils/seasonRegions';
import { formatCompactTonnes } from '../../../utils/formatCompactTonnes';
import { countryLabel } from '../../../utils/countryFilters';
import useDownloadFeedback from '../../../hooks/useDownloadFeedback';
import SeasonCalendar from './SeasonCalendar.jsx';
import LockedBlur from './LockedBlur.jsx';
import { lockedPlaceholderPack } from '../../../utils/seasonLockedPack';
import { triggerPdfDownload } from '../../../utils/triggerPdfDownload';
import { createDownloadGuard } from '../../../utils/downloadGuard';
import { snapshotLoadingText } from '../../../utils/seasonLoadingText';

// Same rule as the PDF: dash when none, "<1" below one tonne.
function formatLyTonnes(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return formatCompactTonnes(n);
}

// Old published summaries carry no last-year tonnes: hide the column instead of a column of dashes.
function hasLastYear(rows) {
  return rows.some((r) => r?.tonnes_ly != null && Number.isFinite(Number(r.tonnes_ly)));
}

function formatTopRegion(v) {
  if (!v?.top_region) return '—';
  const pct = Number(v.top_region_pct);
  return typeof v.top_region_pct === 'number' && Number.isFinite(pct)
    ? `${v.top_region} ${Math.round(pct)}%`
    : v.top_region;
}

function YoyCell({ yoyPct, yoyBasis, yoyFamilyPct, tonnesLy, locked = false }) {
  const { t } = useTranslation();
  const { kind, text, tone } = formatSeasonYoy({ yoyPct, yoyBasis, yoyFamilyPct, tonnesLy });
  const color =
    tone === 'up'
      ? 'text-[#16A34A]'
      : tone === 'down'
        ? 'text-[#EF4444]'
        : 'text-[#828382]';
  const cellText = kind === 'new' ? t('map.scorecards.yoyNew', 'New') : text;
  return (
    <td
      className={`py-1.5 text-right tabular-nums ${locked ? 'text-[#828382]' : color}`}
      title={
        !locked && kind === 'approx'
          ? t(
              'map.scorecards.yoyApproxTitle',
              'Label changed vs last season; compared on all lots containing these cultivars',
            )
          : undefined
      }
    >
      {locked ? <LockedBlur>{cellText}</LockedBlur> : cellText}
    </td>
  );
}

/**
 * Pattern B Season Summary popup (council 2026-10-12).
 * Varieties · region share · peak · YoY · importers · Download season summary.
 */
const SeasonSummaryModal = ({
  isOpen,
  onClose,
  // Season summary PDF Download: map_season_access importers|exporters|all (`all` only).
  canDownloadSeasonPdf = false,
  selectedCommodity = '',
  // Clear when signed-in email changes (Astra account-switch MUST-FIX).
  email = '',
  destSel = null,
  originSel = null,
  // Ask #54: packs from sea_tracking_active (no second season preview CF).
  seasonBundle = null,
  seasonBundleLoading = false,
  seasonBundleError = false,
  // Live Season call status from useSeasonLivePack (owned by ScorecardsBar).
  liveStatus = 'idle',
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const feedback = useDownloadFeedback();
  const pdfBusy = feedback.phase !== 'idle';
  const [pdfErr, setPdfErr] = useState('');
  const [pdfErrIsInfo, setPdfErrIsInfo] = useState(false);

  const commodity = resolveSeasonCommodity(selectedCommodity);
  const seasonDest = resolveSeasonDestKey(destSel);
  const selected = selectSeasonFromActive(
    seasonBundle,
    destSel,
    commodity,
    originSel,
  );

  // Bumped on every download and on every selection/session change; a late mint response from an older one is dropped.
  const downloadGuard = useRef(createDownloadGuard()).current;

  useEffect(() => {
    downloadGuard.bump();
    setPdfErr('');
    setPdfErrIsInfo(false);
    feedback.reset();
  }, [isOpen, commodity, email, seasonDest.key, selected.originKey, selected.scope, i18n?.language, canDownloadSeasonPdf]);

  const loadState = commodity
    ? resolveSeasonChromeState({
        seasonBundle,
        seasonBundleLoading,
        seasonBundleError,
        selected,
        destKey: seasonDest.key,
        liveStatus,
      })
    : 'idle';
  const locked = loadState === 'teaser';
  const preview = locked ? lockedPlaceholderPack(selected.pack) : selected.pack;
  const Val = ({ children }) => (locked ? <LockedBlur>{children}</LockedBlur> : children);

  if (!isOpen) return null;

  const pdfDisabledReason = seasonPdfDisabledReason(loadState, seasonDest.key, preview);
  const pdfDisabledTitle =
    pdfDisabledReason === 'seasonSelectDestination'
      ? t('map.scorecards.seasonSelectDestination', 'Select a destination to see the season summary.')
      : pdfDisabledReason === 'seasonOriginNotPublished'
          ? t(
              'map.scorecards.seasonOriginNotPublished',
              'Season summary is published for Peru, Chile or both. Select Peru and/or Chile as the origin.',
            )
          : pdfDisabledReason === 'seasonScopeNotPublished'
            ? t('map.scorecards.seasonScopeNotPublished', 'Season report not published for this combination yet.')
            : pdfDisabledReason === 'seasonEmpty'
              ? t('map.scorecards.reportEmpty', 'No shipments for this combination in the season window')
              : pdfDisabledReason === 'pdfNotPublished'
                ? t('map.scorecards.pdfNotPublished', 'Season PDF not published yet for this selection')
                : undefined;

  const showImporters = hasSeasonImportersSection(preview);
  const showExporters = hasSeasonExportersSection(preview);
  const varieties = Array.isArray(preview?.varieties) ? preview.varieties : [];
  const regionTables = pickRegionTables(preview);
  const regionDestNames = (
    Array.isArray(preview?.dest_set) && preview.dest_set.length ? preview.dest_set : seasonDest.codes || []
  )
    .map((c) => countryLabel(c))
    .filter(Boolean);
  const regionDestLabel = new Intl.ListFormat(i18n?.language || 'en', { type: 'conjunction' }).format(regionDestNames);
  const hasTopRegion = varieties.some((v) => v?.top_region);
  const importers = showImporters && Array.isArray(preview.importers) ? preview.importers : [];
  const hasImpTopVariety = importers.some((r) => r?.top_variety);
  const hasImpTopRegion = importers.some((r) => r?.top_region);
  const exporters = showExporters && Array.isArray(preview.exporters) ? preview.exporters : [];
  const lyVarieties = hasLastYear(varieties);
  const lyImporters = hasLastYear(importers);
  const lyExporters = hasLastYear(exporters);
  const yoy = preview?.yoy_pct;
  const peakWeek = preview?.peak_week;
  const peakTn = preview?.peak_tonnes;
  const originCountries = Array.isArray(preview?.origins) && preview.origins.length
    ? preview.origins
    : Array.isArray(preview?.origin_countries)
      ? preview.origin_countries
      : preview
        ? ['Peru', 'Chile']
        : [];
  const seasonLabel =
    (typeof preview?.season_label === 'string' && preview.season_label) ||
    (typeof preview?.generated_at === 'string' && preview.generated_at.slice(0, 4)) ||
    null;
  const subtitle = formatSeasonSubtitle({
    destKey: seasonDest.key,
    destCodes: seasonDest.codes,
    originCountries,
    destinationCount: preview?.destination_count ?? null,
    seasonLabel,
  });

  const downloadText =
    feedback.phase === 'preparing'
      ? t('map.scorecards.pdfBusy', 'Downloading…')
      : feedback.phase === 'handoff'
        ? t('map.scorecards.pdfDownloading', 'Downloading…')
        : t('map.scorecards.downloadSeasonSummary', 'Download season summary');

  const onDownload = async () => {
    if (
      !canDownloadSeasonPdf ||
      !commodity ||
      !seasonDest.fetchable ||
      selected.destMissing ||
      selected.originMissing ||
      loadState !== 'ready' ||
      pdfDisabledReason
    ) {
      return;
    }
    const fbGen = feedback.start();
    if (fbGen == null) return;
    const gen = downloadGuard.begin();
    setPdfErr('');
    setPdfErrIsInfo(false);
    try {
      const body = await mintImporterReportDownload({
        destination: selected.scope,
        commodity,
        kind: 'season_to_date',
        lang: pdfLangFromI18n(i18n),
      });
      if (!downloadGuard.isCurrent(gen)) {
        return;
      }
      if (triggerPdfDownload(body.url)) feedback.handoff(fbGen);
      if (body?.lang_fallback && downloadGuard.isCurrent(gen)) {
        setPdfErr(t('map.scorecards.pdfLangFallback', 'Spanish PDF not available yet. The English version was downloaded.'));
        setPdfErrIsInfo(true);
      }
    } catch (e) {
      if (downloadGuard.isCurrent(gen)) {
        setPdfErr(e?.message || t('map.scorecards.pdfError', 'Report unavailable'));
        setPdfErrIsInfo(false);
      }
    } finally {
      feedback.fail(fbGen);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[2200] flex items-center justify-center bg-black/40 p-4 max-lg:pb-[max(1rem,env(safe-area-inset-bottom))]"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="season-summary-modal-title"
        className="flex max-h-[85dvh] w-full max-w-3xl flex-col overflow-hidden rounded-xl max-lg:self-end max-lg:rounded-b-none max-lg:rounded-t-2xl bg-white shadow-xl dark:bg-[#1d1d1d]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-[#E8EBE9] px-5 py-4 dark:border-[#30363d]">
          <div>
            <h2
              id="season-summary-modal-title"
              className="font-['Inter'] text-[16px] font-semibold text-[#282A29] dark:text-[#f0f5f2]"
            >
              {t('map.scorecards.seasonSummaryTitle', 'Season summary')}
            </h2>
            <p className="mt-1 font-['Inter'] text-[11px] text-[#828382]">
              {subtitle}
              {commodity ? (
                <span className="block mt-0.5">
                  {t('map.scorecards.seasonCommodityLine', '{{commodity}} · season to date', {
                    commodity: commodity.replace(/\b\w/g, (c) => c.toUpperCase()),
                  })}
                </span>
              ) : null}
              {preview?.generated_at ? (
                <span className="block mt-0.5">
                  {t('map.scorecards.generatedAt', 'Generated {{when}}', {
                    when: preview.generated_at,
                  })}
                </span>
              ) : null}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="-m-1 inline-flex items-center justify-center rounded-md p-1 text-[#828382] hover:bg-[#F0F5F2] max-lg:min-h-11 max-lg:min-w-11 lg:m-0 dark:hover:bg-[#30363d]"
            aria-label={t('common.close', 'Close')}
          >
            <X size={18} />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-auto px-5 py-3">
          {locked ? (
            <div role="status" className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[#E8EBE9] px-3 py-2 font-['Inter'] text-[12px] text-[#5d625f] dark:border-[#30363d] dark:text-[#a8b0ab]">
              <span>{t('map.scorecards.lockedBenefit', 'Unlock the full season summary with the Elite plan')}</span>
              <button
                type="button"
                onClick={() => {
                  onClose();
                  navigate('/billing');
                }}
                className="inline-flex min-h-9 items-center rounded-md bg-bloom-green px-3 text-[12px] font-semibold text-white hover:opacity-90"
              >
                {t('map.scorecards.upgradeElite', 'Upgrade to Elite')}
              </button>
            </div>
          ) : null}
          {loadState === 'loading' ? (
            <p className="text-sheen font-['Inter'] text-[13px] text-[#828382]">
              {snapshotLoadingText(t)}
            </p>
          ) : null}
          {loadState === 'error' ? (
            <p className="font-['Inter'] text-[13px] text-[#828382]">
              {t('map.scorecards.seasonError', 'Season summary could not be loaded.')}
            </p>
          ) : null}
          {loadState === 'unavailable' ? (
            <p className="font-['Inter'] text-[13px] text-[#828382]">
              {t(
                'map.scorecards.seasonUnavailable',
                'Season summary is temporarily unavailable. Refresh the map to try again.',
              )}
            </p>
          ) : null}
          {loadState === 'origin_unsupported' ? (
            <p className="font-['Inter'] text-[13px] text-[#828382]">
              {t(
                'map.scorecards.seasonOriginNotPublished',
                'Season summary is published for Peru, Chile or both. Select Peru and/or Chile as the origin.',
              )}
            </p>
          ) : null}
          {loadState === 'dest_unsupported' ? (
            <p className="font-['Inter'] text-[13px] text-[#828382]">
              {t(
                'map.scorecards.seasonSelectDestination',
                'Select a destination to see the season summary.',
              )}
            </p>
          ) : null}
          {loadState === 'empty' ? (
            <p className="font-['Inter'] text-[13px] text-[#828382]">
              {t('map.scorecards.reportEmpty', 'No shipments for this combination in the season window')}
            </p>
          ) : null}
          {loadState === 'missing' ? (
            <p className="font-['Inter'] text-[13px] text-[#828382]">
              {t(
                'map.scorecards.seasonScopeNotPublished',
                'Season report not published for this combination yet.',
              )}
            </p>
          ) : null}
          {loadState === 'hidden' ? (
            <p className="font-['Inter'] text-[13px] text-[#828382]">
              {t('map.scorecards.seasonAccessHidden', 'Season summary is not available for this account.')}
            </p>
          ) : null}
          {loadState === 'metrics' ? (
            <p className="font-['Inter'] text-[13px] text-[#828382]">
              {t('map.scorecards.seasonMetricsEmpty', 'Season metrics coming soon')}
            </p>
          ) : null}

          <SeasonCalendar seasonCalendar={preview?.season_calendar} />

          {loadState === 'ready' || locked ? (
            <>

              <div className="font-['Inter'] text-[12px] text-[#5d625f] dark:text-[#a8b0ab]">
                {peakWeek ? (
                  <span>
                    <Val>
                      {t('map.scorecards.peakWeekLine', 'Peak volume: week {{week}} · {{tn}} tn/week', {
                        week: peakWeek,
                        tn: formatCompactTonnes(peakTn),
                      })}
                    </Val>
                  </span>
                ) : null}
                {yoy != null ? (
                  <span className="ml-2">
                    <Val>
                    {yoy > 2
                      ? t('map.scorecards.aheadYoY', '{{pct}}% ahead vs last year', {
                          pct: Math.abs(yoy).toFixed(0),
                        })
                      : yoy < -2
                        ? t('map.scorecards.behindYoY', '{{pct}}% behind vs last year', {
                            pct: Math.abs(yoy).toFixed(0),
                          })
                        : t('map.scorecards.inLineYoY', 'Roughly in line vs last year')}
                    </Val>
                  </span>
                ) : null}
              </div>

              {(preview?.outlook_share?.ok || preview?.outlook_fob?.ok || preview?.outlook_terminal?.ok) ? (
                <section className="font-['Inter'] text-[12px] leading-[1.45] text-[#5d625f] dark:text-[#a8b0ab]">
                  <h3 className="mb-1 font-semibold text-[#282A29] dark:text-[#f0f5f2]">
                    {t('map.scorecards.usaOutlookTitle', 'USA historical outlook — week {{week}}', {
                      week: locked ? '—' : preview?.as_of_week || peakWeek || '—',
                    })}
                  </h3>
                  <div aria-hidden={locked || undefined} className={locked ? 'select-none blur-[5px]' : undefined}>
                  {preview?.outlook_share?.ok ? (
                    <p>
                      {t(
                        'map.scorecards.usaOutlookShare',
                        'Peru is about {{ty}}% of USA movement this season versus {{ly}}% last season.',
                        {
                          ty: Number(preview.outlook_share.peru_ty_pct).toFixed(0),
                          ly: Number(preview.outlook_share.peru_ly_pct).toFixed(0),
                        },
                      )}
                    </p>
                  ) : null}
                  {preview?.outlook_fob?.ok ? (
                    <p>
                      {t(
                        'map.scorecards.usaOutlookFob',
                        'Bloom intelligence points out that prices are highest around week {{week}} (about ${{usd}} per flat) on the three-year average USA FOB.',
                        {
                          usd: Number(preview.outlook_fob.expensive_usd).toFixed(0),
                          week: preview.outlook_fob.expensive_week,
                        },
                      )}
                    </p>
                  ) : null}
                  {preview?.outlook_fob?.ok && Array.isArray(preview.outlook_fob.next8) && preview.outlook_fob.next8.length >= 2 ? (
                    <p>
                      {t(
                        'map.scorecards.usaOutlookNext8',
                        'Over the next eight weeks (weeks {{w1}}–{{w8}}), that same quote has historically been about ${{first}} then about ${{last}} by week {{lastW}}.',
                        {
                          w1: preview.outlook_fob.next8[0].week,
                          w8: preview.outlook_fob.next8[preview.outlook_fob.next8.length - 1].week,
                          first: Number(preview.outlook_fob.next8[0].usd).toFixed(0),
                          last: Number(preview.outlook_fob.next8[preview.outlook_fob.next8.length - 1].usd).toFixed(0),
                          lastW: preview.outlook_fob.next8[preview.outlook_fob.next8.length - 1].week,
                        },
                      )}
                    </p>
                  ) : null}
                  {preview?.outlook_terminal?.ok && preview.outlook_terminal.city ? (
                    <p>
                      {t(
                        'map.scorecards.usaOutlookTerminal',
                        'The highest three-year-average USA terminal price is about ${{usd}} per flat in {{city}}, week {{week}}.',
                        {
                          usd: Number(preview.outlook_terminal.expensive_usd).toFixed(0),
                          city: preview.outlook_terminal.city,
                          week: preview.outlook_terminal.expensive_week,
                        },
                      )}
                    </p>
                  ) : null}
                  {preview?.outlook_terminal?.ok && preview.outlook_terminal.ty_city && preview.outlook_terminal.ty_expensive_usd != null ? (
                    <p>
                      {t(
                        Number(preview.outlook_terminal.ty_expensive_usd) > Number(preview.outlook_terminal.expensive_usd) + 0.5
                          ? 'map.scorecards.usaOutlookTerminalTyAbove'
                          : Number(preview.outlook_terminal.ty_expensive_usd) < Number(preview.outlook_terminal.expensive_usd) - 0.5
                            ? 'map.scorecards.usaOutlookTerminalTyBelow'
                            : 'map.scorecards.usaOutlookTerminalTyInline',
                        "This season's highest terminal price is about ${{usd}} per flat in {{city}}, week {{week}}, {{vs}} that three-year peak of about ${{hist}}.",
                        {
                          usd: Number(preview.outlook_terminal.ty_expensive_usd).toFixed(0),
                          city: preview.outlook_terminal.ty_city,
                          week: preview.outlook_terminal.ty_expensive_week,
                          hist: Number(preview.outlook_terminal.expensive_usd).toFixed(0),
                          vs:
                            Number(preview.outlook_terminal.ty_expensive_usd) > Number(preview.outlook_terminal.expensive_usd) + 0.5
                              ? 'above'
                              : Number(preview.outlook_terminal.ty_expensive_usd) < Number(preview.outlook_terminal.expensive_usd) - 0.5
                                ? 'below'
                                : 'in line with',
                        },
                      )}
                    </p>
                  ) : null}
                  {preview?.outlook_terminal?.ok && preview.outlook_terminal.city && Array.isArray(preview.outlook_terminal.next8) && preview.outlook_terminal.next8.length >= 2 ? (
                    <p>
                      {t(
                        'map.scorecards.usaOutlookTerminalNext8',
                        'Over the next eight weeks (weeks {{w1}}–{{w8}}), that same terminal quote in {{city}} has historically been about ${{first}} then about ${{last}} by week {{lastW}}.',
                        {
                          city: preview.outlook_terminal.city,
                          w1: preview.outlook_terminal.next8[0].week,
                          w8: preview.outlook_terminal.next8[preview.outlook_terminal.next8.length - 1].week,
                          first: Number(preview.outlook_terminal.next8[0].usd).toFixed(0),
                          last: Number(preview.outlook_terminal.next8[preview.outlook_terminal.next8.length - 1].usd).toFixed(0),
                          lastW: preview.outlook_terminal.next8[preview.outlook_terminal.next8.length - 1].week,
                        },
                      )}
                    </p>
                  ) : null}
                  </div>
                </section>
              ) : null}

              <section>
                <h3 className="mb-2 font-['Inter'] text-[12px] font-semibold text-[#282A29] dark:text-[#f0f5f2]">
                  {t('map.scorecards.varietyShareTitle', 'Variety share of season volume')}
                </h3>
                {varieties.length === 0 ? (
                  <span className="text-[12px] text-[#828382]">—</span>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[360px] border-collapse font-['Inter'] text-[12px]">
                      <thead>
                        <tr className="border-b border-[#E8EBE9] text-left text-[#828382] dark:border-[#30363d]">
                          <th className="py-1 font-medium">{t('map.scorecards.colVariety', 'Variety')}</th>
                          <th className="py-1 text-right font-medium">{t('map.scorecards.colTonnes', 'Tonnes')}</th>
                          {lyVarieties && <th className="py-1 text-right font-medium">{t('map.scorecards.colLastYear', 'Last year')}</th>}
                          <th className="py-1 text-right font-medium">{t('map.scorecards.colShare', 'Share')}</th>
                          <th className="whitespace-nowrap py-1 text-right font-medium">
                            {t('map.scorecards.colVsLastSeason', 'vs last season')}
                          </th>
                          {hasTopRegion && (
                            <th className="whitespace-nowrap py-1 pl-3 text-right font-medium">
                              {t('map.scorecards.colTopRegion', 'Top region')}
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {varieties.map((v, i) => {
                          const isOther = String(v.name || '').toLowerCase() === 'other';
                          return (
                            <tr
                              key={`${i}:${v.name || 'variety'}`}
                              className="border-b border-[#F0F5F2] dark:border-[#30363d]"
                            >
                              <td
                                className={`py-1.5 text-[#282A29] dark:text-[#f0f5f2] ${
                                  isOther ? 'italic text-[#5d625f] dark:text-[#a8b0ab]' : ''
                                }`}
                              >
                                {v.name}
                              </td>
                              <td className="py-1.5 text-right tabular-nums">
                                <Val>{formatCompactTonnes(v.tonnes)}</Val>
                              </td>
                              {lyVarieties && <td className="py-1.5 text-right tabular-nums"><Val>{formatLyTonnes(v.tonnes_ly)}</Val></td>}
                              <td className="py-1.5 text-right tabular-nums">
                                <Val>{Number(v.share || 0).toFixed(1)}%</Val>
                              </td>
                              <YoyCell yoyPct={v.yoy_pct} yoyBasis={v.yoy_basis} yoyFamilyPct={v.yoy_family_pct} tonnesLy={v.tonnes_ly} locked={locked} />
                              {hasTopRegion && (
                                <td className="whitespace-nowrap py-1.5 pl-3 text-right tabular-nums">
                                  <Val>{formatTopRegion(v)}</Val>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {regionTables.map((tbl) => (
                <section key={tbl.kind}>
                  <h3 className="mb-2 font-['Inter'] text-[12px] font-semibold text-[#282A29] dark:text-[#f0f5f2]">
                    {tbl.kind === 'dest'
                      ? t('map.scorecards.regionShareDestTitle', 'Region share of Peru exports to {{dest}}', {
                          dest: regionDestLabel || countryLabel(tbl.dest),
                        })
                      : t('map.scorecards.regionShareTitle', 'Region share of Peru exports (all destinations)')}
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[360px] border-collapse font-['Inter'] text-[12px]">
                      <thead>
                        <tr className="border-b border-[#E8EBE9] text-left text-[#828382] dark:border-[#30363d]">
                          <th className="py-1 font-medium">{t('map.scorecards.colRegion', 'Region')}</th>
                          <th className="py-1 text-right font-medium">{t('map.scorecards.colTonnes', 'Tonnes')}</th>
                          <th className="py-1 text-right font-medium">{t('map.scorecards.colShare', 'Share')}</th>
                          <th className="whitespace-nowrap py-1 text-right font-medium">
                            {t('map.scorecards.colVsLastSeason', 'vs last season')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {tbl.rows.map((r, i) => (
                          <tr key={`${i}:${r.region}`} className="border-b border-[#F0F5F2] dark:border-[#30363d]">
                            <td className="py-1.5 text-[#282A29] dark:text-[#f0f5f2]"><Val>{r.region}</Val></td>
                            <td className="py-1.5 text-right tabular-nums"><Val>{formatCompactTonnes(r.tonnes)}</Val></td>
                            <td className="py-1.5 text-right tabular-nums"><Val>{Number(r.share || 0).toFixed(0)}%</Val></td>
                            <YoyCell yoyPct={r.yoy_pct} yoyBasis={r.yoy_basis} yoyFamilyPct={r.yoy_family_pct} tonnesLy={r.tonnes_ly} locked={locked} />
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              ))}

              {showImporters ? (
                <section>
                  <h3 className="mb-2 font-['Inter'] text-[12px] font-semibold text-[#282A29] dark:text-[#f0f5f2]">
                    {t('map.scorecards.topImporters', 'Top importers')}
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[360px] border-collapse font-['Inter'] text-[12px]">
                      <thead>
                        <tr className="border-b border-[#E8EBE9] text-left text-[#828382] dark:border-[#30363d]">
                          <th className="py-1 font-medium">{t('map.scorecards.colImporter', 'Importer')}</th>
                          <th className="py-1 text-right font-medium">{t('map.scorecards.colTonnes', 'Tonnes')}</th>
                          {lyImporters && <th className="py-1 text-right font-medium">{t('map.scorecards.colLastYear', 'Last year')}</th>}
                          <th className="py-1 text-right font-medium">{t('map.scorecards.colShare', 'Share')}</th>
                          <th className="whitespace-nowrap py-1 text-right font-medium">
                            {t('map.scorecards.colVsLastSeason', 'vs last season')}
                          </th>
                          {hasImpTopVariety && (
                            <th className="whitespace-nowrap py-1 pl-3 text-right font-medium">
                              {t('map.scorecards.colTopVariety', 'Top variety')}
                            </th>
                          )}
                          {hasImpTopRegion && (
                            <th className="whitespace-nowrap py-1 pl-3 text-right font-medium">
                              {t('map.scorecards.colTopRegion', 'Top region')}
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {importers.map((r, i) => (
                          <tr key={`${i}:${r.name}`} className="border-b border-[#F0F5F2] dark:border-[#30363d]">
                            <td className="py-1.5 text-[#282A29] dark:text-[#f0f5f2]"><Val>{r.name}</Val></td>
                            <td className="py-1.5 text-right tabular-nums"><Val>{formatCompactTonnes(r.tonnes)}</Val></td>
                            {lyImporters && <td className="py-1.5 text-right tabular-nums"><Val>{formatLyTonnes(r.tonnes_ly)}</Val></td>}
                            <td className="py-1.5 text-right tabular-nums"><Val>{Number(r.share || 0).toFixed(0)}%</Val></td>
                            <YoyCell yoyPct={r.yoy_pct} yoyBasis={r.yoy_basis} yoyFamilyPct={r.yoy_family_pct} tonnesLy={r.tonnes_ly} locked={locked} />
                            {hasImpTopVariety && (
                              <td className="whitespace-nowrap py-1.5 pl-3 text-right"><Val>{r.top_variety || '—'}</Val></td>
                            )}
                            {hasImpTopRegion && (
                              <td className="whitespace-nowrap py-1.5 pl-3 text-right tabular-nums"><Val>{formatTopRegion(r)}</Val></td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              ) : null}

              {showExporters ? (
                <section>
                  <h3 className="mb-2 font-['Inter'] text-[12px] font-semibold text-[#282A29] dark:text-[#f0f5f2]">
                    {t('map.scorecards.topExporters', 'Top exporters')}
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[360px] border-collapse font-['Inter'] text-[12px]">
                      <thead>
                        <tr className="border-b border-[#E8EBE9] text-left text-[#828382] dark:border-[#30363d]">
                          <th className="py-1 font-medium">{t('map.scorecards.colExporter', 'Exporter')}</th>
                          <th className="py-1 text-right font-medium">{t('map.scorecards.colTonnes', 'Tonnes')}</th>
                          {lyExporters && <th className="py-1 text-right font-medium">{t('map.scorecards.colLastYear', 'Last year')}</th>}
                          <th className="py-1 text-right font-medium">{t('map.scorecards.colShare', 'Share')}</th>
                          <th className="whitespace-nowrap py-1 text-right font-medium">
                            {t('map.scorecards.colVsLastSeason', 'vs last season')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {exporters.map((r, i) => (
                          <tr key={`${i}:${r.name}`} className="border-b border-[#F0F5F2] dark:border-[#30363d]">
                            <td className="py-1.5 text-[#282A29] dark:text-[#f0f5f2]"><Val>{r.name}</Val></td>
                            <td className="py-1.5 text-right tabular-nums"><Val>{formatCompactTonnes(r.tonnes)}</Val></td>
                            {lyExporters && <td className="py-1.5 text-right tabular-nums"><Val>{formatLyTonnes(r.tonnes_ly)}</Val></td>}
                            <td className="py-1.5 text-right tabular-nums"><Val>{Number(r.share || 0).toFixed(0)}%</Val></td>
                            <YoyCell yoyPct={r.yoy_pct} yoyBasis={r.yoy_basis} yoyFamilyPct={r.yoy_family_pct} tonnesLy={r.tonnes_ly} locked={locked} />
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              ) : null}
            </>
          ) : null}
        </div>

        <div className="flex flex-col gap-2 border-t border-[#E8EBE9] px-5 py-3 dark:border-[#30363d]">
          {pdfErr ? <div role={pdfErrIsInfo ? 'status' : undefined} className={`font-['Inter'] break-words text-[11px] ${pdfErrIsInfo ? 'text-[#6B7280] dark:text-gray-400' : 'text-[#EF4444]'}`}>{pdfErr}</div> : null}
          {canDownloadSeasonPdf && pdfDisabledTitle && !pdfBusy ? (
            <div className="font-['Inter'] break-words text-[11px] text-[#828382] lg:hidden">{pdfDisabledTitle}</div>
          ) : null}
          <div className="flex flex-wrap items-center justify-end gap-2">
            <span role="status" aria-live="polite" className="sr-only">
              {pdfBusy ? downloadText : ''}
            </span>
            {canDownloadSeasonPdf ? (
              <button
                type="button"
                onClick={onDownload}
                disabled={!commodity || loadState !== 'ready' || Boolean(pdfDisabledReason)}
                aria-disabled={pdfBusy || undefined}
                title={pdfDisabledTitle}
                className="inline-flex min-h-11 min-w-[250px] flex-1 items-center justify-center gap-1 rounded-[6px] border border-[#E8EBE9] bg-white px-3 py-2 font-['Inter'] text-[12px] font-semibold text-[#282A29] hover:border-bloom-green/60 disabled:opacity-50 lg:min-h-0 lg:flex-none dark:border-[#30363d] dark:bg-[#1d1d1d] dark:text-[#f0f5f2]"
              >
                {feedback.phase === 'preparing' ? (
                  <Loader2 size={14} aria-hidden="true" className="animate-spin text-bloom-green motion-reduce:animate-none" />
                ) : feedback.phase === 'handoff' ? (
                  <Check size={14} aria-hidden="true" className="text-bloom-green" />
                ) : (
                  <Download size={14} aria-hidden="true" className="text-bloom-green" />
                )}
                {downloadText}
              </button>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              className="min-h-11 flex-1 rounded-lg bg-bloom-green px-4 py-2 font-['Inter'] text-[12px] font-medium text-white hover:bg-bloom-green-hover lg:min-h-0 lg:flex-none"
            >
              {t('common.done', 'Done')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeasonSummaryModal;
